import React from 'react';
import { User, Calendar, Settings, FileText, Info } from 'lucide-react';
import { formatDisplayValue } from '../../utils/displayUtils';
import { getWorkerColor, getWorkerInitials, getWorkerName } from '../../utils/workerUtils';
import HandoffSettlementDetails from './HandoffSettlementDetails';

const JobQueueExpandedDetails = ({
  job,
  openSections,
  onToggleSection,
  onPreviewImages,
  t,
  workers,
  partners,
  currentCompanyId,
  formatDate,
  formatDuration,
  filterPlaceholder,
  getDisplayStatus,
  hasRealUpdate,
  onHandoffArchived
}) => {
  if (!job) return null;

  const openSection = openSections[job._id];

  return (
    <div
      className="mt-2 pt-2 border-t border-gray-600 bg-gray-800 rounded p-3"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-2">
        <div className="bg-gray-700 rounded border border-gray-600">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSection(job._id, 'clientInfo');
            }}
            className="flex w-full items-center justify-between px-2 py-1.5 text-left"
          >
            <span className="flex items-center gap-1 text-xs font-semibold text-white">
              <User className="h-3 w-3" />
              {t('jobs.clientInfo')}
            </span>
            <svg
              className={`h-3 w-3 text-gray-400 transition-transform transform ${openSection === 'clientInfo' ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {openSection === 'clientInfo' && (
            <div className="border-t border-gray-600 px-2 py-1.5">
              <div className="text-[10px] text-gray-300 space-y-1">
                <div className="flex gap-2">
                  <span className="text-gray-400 min-w-[50px]">{t('jobs.name')}:</span>
                  <span className="flex-1">{formatDisplayValue(job.clientName)}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 min-w-[50px]">{t('jobs.phone')}:</span>
                  <span className="flex-1">
                    {filterPlaceholder(job.clientPhone) ? (
                      <a
                        href={`tel:${job.clientPhone.replace(/\s+/g, '')}`}
                        className="text-blue-300 hover:text-blue-200 underline underline-offset-2"
                      >
                        {job.clientPhone}
                      </a>
                    ) : (
                      ''
                    )}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 min-w-[50px]">{t('jobs.email')}:</span>
                  <span className="flex-1 break-all">
                    {job.clientEmail && job.clientEmail !== 'no-email@example.com' && job.clientEmail.trim()
                      ? job.clientEmail
                      : ''}
                  </span>
                </div>
                {job.clientAddress && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[50px]">{t('jobs.address')}:</span>
                    <span className="flex-1">{job.clientAddress}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {job.deviceType && (
          <div className="bg-gray-700 rounded border border-gray-600">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSection(job._id, 'deviceInfo');
              }}
              className="flex w-full items-center justify-between px-2 py-1.5 text-left"
            >
              <span className="flex items-center gap-1 text-xs font-semibold text-white">
                <Settings className="h-3 w-3" />
                {t('jobs.deviceInfo')}
              </span>
              <svg
                className={`h-3 w-3 text-gray-400 transition-transform transform ${openSection === 'deviceInfo' ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openSection === 'deviceInfo' && (
              <div className="border-t border-gray-600 px-2 py-1.5">
                <div className="text-[10px] text-gray-300 space-y-1">
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[50px]">{t('jobs.deviceType')}:</span>
                    <span className="flex-1">{filterPlaceholder(job.deviceType)}</span>
                  </div>
                  {filterPlaceholder(job.deviceBrand) && (
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[50px]">{t('jobs.deviceBrand')}:</span>
                      <span className="flex-1">{job.deviceBrand}</span>
                    </div>
                  )}
                  {filterPlaceholder(job.deviceModel) && (
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[50px]">{t('jobs.deviceModel')}:</span>
                      <span className="flex-1">{job.deviceModel}</span>
                    </div>
                  )}
                  {filterPlaceholder(job.deviceSerialNumber) && (
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[50px]">{t('jobs.deviceSerialNumber')}:</span>
                      <span className="flex-1">{job.deviceSerialNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-gray-700 rounded border border-gray-600">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSection(job._id, 'serviceDetails');
            }}
            className="flex w-full items-center justify-between px-2 py-1.5 text-left"
          >
            <span className="flex items-center gap-1 text-xs font-semibold text-white">
              <Calendar className="h-3 w-3" />
              {t('jobs.serviceDetails')}
            </span>
            <svg
              className={`h-3 w-3 text-gray-400 transition-transform transform ${openSection === 'serviceDetails' ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {openSection === 'serviceDetails' && (
            <div className="border-t border-gray-600 px-2 py-1.5">
              <div className="text-[10px] text-gray-300 space-y-1">
                {(job.isReturnVisit || job.lastVisitDate) && job.lastVisitDate && (
                  <div className="flex gap-2 mb-2 pb-2 border-b border-purple-600 bg-purple-900/20 rounded p-2">
                    <span className="text-purple-300 min-w-[80px] font-medium">Datum posete:</span>
                    <span className="flex-1 text-purple-200 font-bold">{formatDate(job.lastVisitDate)}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-gray-400 min-w-[50px]">{t('jobs.date')}:</span>
                  <span className="flex-1">{job.serviceDate ? formatDate(job.serviceDate) : t('jobs.notScheduled')}</span>
                </div>
                {job.scheduledTime && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[50px]">{t('jobs.time')}:</span>
                    <span className="flex-1">{job.scheduledTime}</span>
                  </div>
                )}
                {job.estimatedDuration && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[50px]">{t('jobs.duration')}:</span>
                    <span className="flex-1 text-blue-400">{formatDuration(job.estimatedDuration)}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-gray-400 min-w-[50px]">{t('jobs.serviceLocation')}:</span>
                  <span className="flex-1">{job.serviceLocation || t('jobs.onSiteAddress')}</span>
                </div>
                {filterPlaceholder(job.assignedTo) && (
                  <div className="flex gap-2 items-center">
                    <span className="text-gray-400 min-w-[50px]">{t('jobs.assignedTo')}:</span>
                    <div className="flex-1 flex items-center gap-2">
                      <div
                        className="h-5 w-5 rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0 shadow-sm"
                        style={{
                          backgroundColor: getWorkerColor(job.assignedTo, workers),
                          background: getWorkerColor(job.assignedTo, workers)
                        }}
                        title={getWorkerName(job.assignedTo, workers, partners)}
                      >
                        {getWorkerInitials(job.assignedTo, workers, partners)}
                      </div>
                      <span>{getWorkerName(job.assignedTo, workers, partners)}</span>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="text-gray-400 min-w-[50px]">{t('jobs.status')}:</span>
                  <span className="flex-1">{getDisplayStatus(job)}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 min-w-[50px]">{t('jobs.priority')}:</span>
                  <span className="flex-1">{job.priority}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {job.issueDescription && (
          <div className="bg-gray-700 rounded border border-gray-600">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSection(job._id, 'issueDescription');
              }}
              className="flex w-full items-center justify-between px-2 py-1.5 text-left"
            >
              <span className="flex items-center gap-1 text-xs font-semibold text-white">
                <FileText className="h-3 w-3" />
                {t('jobs.issueDescription')}
              </span>
              <svg
                className={`h-3 w-3 text-gray-400 transition-transform transform ${openSection === 'issueDescription' ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openSection === 'issueDescription' && (
              <div className="border-t border-gray-600 px-2 py-1.5 space-y-1">
                {(job.deviceType || job.deviceBrand || job.deviceModel || job.deviceSerialNumber) && (
                  <>
                    <p className="text-[9px] text-gray-400 font-semibold">Podaci o aparatu:</p>
                    {job.deviceType && (
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-gray-400">{t('jobs.deviceType')}:</span>
                        <span className="text-white">{filterPlaceholder(job.deviceType)}</span>
                      </div>
                    )}
                    {job.deviceBrand && filterPlaceholder(job.deviceBrand) && (
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-gray-400">{t('jobs.deviceBrand')}:</span>
                        <span className="text-white">{job.deviceBrand}</span>
                      </div>
                    )}
                    {job.deviceModel && filterPlaceholder(job.deviceModel) && (
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-gray-400">{t('jobs.deviceModel')}:</span>
                        <span className="text-white">{job.deviceModel}</span>
                      </div>
                    )}
                    {job.deviceSerialNumber && filterPlaceholder(job.deviceSerialNumber) && job.deviceSerialNumber !== 'N/A' && (
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-gray-400">{t('jobs.deviceSerialNumber')}:</span>
                        <span className="text-white">{job.deviceSerialNumber}</span>
                      </div>
                    )}
                  </>
                )}
                {Array.isArray(job.deviceImages) && job.deviceImages.length > 0 ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviewImages(job);
                    }}
                    className="mt-1 text-[10px] text-blue-400 hover:text-blue-300 underline"
                  >
                    Vidi slike ({job.deviceImages.length})
                  </button>
                ) : (
                  <p className="mt-1 text-[9px] text-gray-500 italic">Nema priloženih slika.</p>
                )}
                <div>
                  <p className="text-[9px] text-gray-400 font-semibold mb-1">Opis problema:</p>
                  <p className="text-[10px] text-white whitespace-pre-wrap">{filterPlaceholder(job.issueDescription)}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {job.usedSpareParts && job.usedSpareParts.length > 0 && (
          <div className="bg-gray-700 rounded border border-gray-600">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSection(job._id, 'spareParts');
              }}
              className="flex w-full items-center justify-between px-2 py-1.5 text-left"
            >
              <span className="text-xs font-semibold text-white">{t('jobs.usedSpareParts')}</span>
              <svg
                className={`h-3 w-3 text-gray-400 transition-transform transform ${openSection === 'spareParts' ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openSection === 'spareParts' && (
              <div className="border-t border-gray-600 px-2 py-1.5">
                <div className="text-[10px] text-gray-300">
                  {job.usedSpareParts.length} {job.usedSpareParts.length === 1 ? 'korišćen deo' : 'korišćenih delova'}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-gray-700 rounded border border-gray-600">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSection(job._id, 'metadata');
            }}
            className="flex w-full items-center justify-between px-2 py-1.5 text-left"
          >
            <span className="flex items-center gap-1 text-xs font-semibold text-white">
              <Info className="h-3 w-3" />
              {t('jobs.jobMetadata')}
            </span>
            <svg
              className={`h-3 w-3 text-gray-400 transition-transform transform ${openSection === 'metadata' ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {openSection === 'metadata' && (
            <div className="border-t border-gray-600 px-2 py-1.5">
              <div className="text-[10px] text-gray-300 space-y-1">
                {job.creator && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[60px]">{t('jobs.createdBy')}:</span>
                    <span className="flex-1">{job.creator}</span>
                  </div>
                )}
                {(job.createdByEmail || job.createdByName) && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[60px]">Kreirao:</span>
                    <span className="flex-1">{job.createdByEmail || job.createdByName}</span>
                  </div>
                )}
                {job.createdAt && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[60px]">{t('jobs.createdAt')}:</span>
                    <span className="flex-1">{new Date(job.createdAt).toLocaleString('sr-RS')}</span>
                  </div>
                )}
                {hasRealUpdate(job) && (job.lastUpdatedByEmail || job.lastUpdatedByName) && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[60px]">Izmenio:</span>
                    <span className="flex-1">{job.lastUpdatedByEmail || job.lastUpdatedByName}</span>
                  </div>
                )}
                {hasRealUpdate(job) && job.updatedAt && (
                  <div className="flex gap-2">
                    <span className="text-gray-400 min-w-[60px]">{t('jobs.updatedAt')}:</span>
                    <span className="flex-1">{new Date(job.updatedAt).toLocaleString('sr-RS')}</span>
                  </div>
                )}
                {Array.isArray(job.activityLog) && job.activityLog.length > 0 && (
                  <div className="pt-1 border-t border-gray-600">
                    <div className="text-gray-400 mb-1">Istorija izmena:</div>
                    {job.activityLog
                      .slice()
                      .reverse()
                      .slice(0, 8)
                      .map((entry, index) => (
                        <div key={`${entry.at || 'entry'}_${index}`} className="flex gap-2">
                          <span className="text-gray-500 min-w-[60px]">
                            {entry.at ? new Date(entry.at).toLocaleString('sr-RS') : '-'}
                          </span>
                          <span className="flex-1">
                            {(entry.byEmail || entry.byName || 'Nepoznato')} ({entry.action || 'updated'})
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <HandoffSettlementDetails
          job={job}
          currentCompanyId={currentCompanyId}
          partners={partners}
          onArchived={onHandoffArchived}
        />
      </div>
    </div>
  );
};

export default JobQueueExpandedDetails;
